import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '~/components';
import { Popover } from '~/components/Popover';
import { chevron } from '~/components/styles';
import { ArtistContactInfo } from '~/modules/artist-roster/components/ArtistContactInfo/ArtistContactInfo';
import type { ArtistEntityType } from '~/modules/artist-roster/types';
import { useAnalytics } from '~/modules/monitoring/analytics/useAnalytics';

interface ContactInfoPopoverProps {
  partyId: number;
  entityType: ArtistEntityType;
}

export function ContactInfoPopover({
  partyId,
  entityType,
}: ContactInfoPopoverProps) {
  const { t } = useTranslation('roster');
  const [isOpen, setIsOpen] = useState(false);
  const { trackEvent } = useAnalytics('Artist Roster');

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);

    if (open) {
      // NOTE: If <ContactInfoPopover /> is used outside the Artist Page, then
      //       we'll have to pass in the source to it as a prop.
      trackEvent('contactInfoViewed', {
        source: 'Profile Page',
        entity_type: entityType,
      });
    }
  };

  return (
    <Popover
      side="bottom"
      onOpenChange={handleOpenChange}
      content={<ArtistContactInfo {...{ partyId, entityType }} />}
      stopPropagation
      triggerAsChild
    >
      <button
        aria-label={t('labels.contactInfo')}
        className="relative flex items-center gap-md rounded-full border py-xs pl-md pr-xl transition-colors border-emphasis-strong hover:bg-emphasis-subtle"
      >
        <div className="mr-xs font-semibold text-default">
          {t('labels.contactInfo')}
        </div>
        <Icon
          className={chevron({ state: isOpen ? 'open' : 'default' })}
          size="sm"
          type="chevron-down"
        />
      </button>
    </Popover>
  );
}

ContactInfoPopover.displayName = 'ContactInfoPopover';
